import type { CSSProperties, ReactNode } from "react";

/**
 * The isometric city of the login panel (Figma "City", 89:1577 in the
 * animation file): the buildings rise from the depot outwards, then the
 * depot's rings spread and a few windows flicker. The tracking layer on top
 * of it (vehicles, pins, alerts, cards) is LoginArtwork's.
 *
 * Built from data rather than the exported vectors: every building sits on
 * the same isometric grid, so a row of [a, b, height, roof, windows] redraws
 * it exactly and lets each one animate as a unit.
 *
 * Painted in layers: the ground (SVG), the depot's rings on it (under the
 * buildings, so a building in front hides them), the buildings (SVG, still
 * once risen) and the windows that light up. The looping parts are HTML
 * boxes placed in percentages of the city, animating only transform and
 * opacity: the browser runs those on the compositor, where SVG elements
 * would be restyled and repainted every frame. The classes and keyframes are
 * in styles.css ("Login city").
 */

/** The part of the 751x976 Figma frame the city occupies. */
export const CITY_VIEW = { x: 20, y: 400, width: 711, height: 430 } as const;
const VIEW = CITY_VIEW;

/** One grid step along a (down-right) is (44, 22); along b (down-left)
 *  (-44, 22). A building's footprint is 58 x 29, its front corner at
 *  (375.5, 506.5) for a = b = 0. */
const STEP_X = 44;
const STEP_Y = 22;
const ORIGIN = { x: 375.5, y: 506.5 } as const;
const HALF_WIDTH = 29;
const HALF_DEPTH = 14.5;

const DEPOT = { a: 3, b: 3 } as const;

type Roof = 0 | 1 | 2 | 3 | "depot";

/** [a, b, wall height, roof tone, windows], back to front as painted. */
const BUILDINGS: ReadonlyArray<readonly [number, number, number, Roof, 0 | 1]> = [
  [0, 0, 59.4, 2, 1], [0, 1, 71.8, 2, 1], [1, 0, 25.5, 1, 0], [0, 2, 18.3, 2, 0],
  [1, 1, 63.3, 0, 1], [2, 0, 25.6, 2, 0], [0, 3, 12.8, 0, 0], [1, 2, 57.5, 3, 1],
  [2, 1, 21.5, 1, 0], [3, 0, 47.6, 3, 1], [0, 4, 57.4, 0, 1], [1, 3, 10.1, 0, 0],
  [2, 2, 26.3, 2, 0], [3, 1, 56.5, 0, 1], [4, 0, 19.7, 2, 0], [0, 5, 16.4, 1, 0],
  [1, 4, 15.1, 3, 0], [2, 3, 8, 0, 0], [3, 2, 18.4, 1, 0], [4, 1, 20.5, 3, 0],
  [5, 0, 8, 1, 0], [0, 6, 24.2, 0, 0], [1, 5, 67.5, 3, 1], [2, 4, 20.1, 0, 0],
  [3, 3, 38, "depot", 0], [4, 2, 50.8, 3, 1], [5, 1, 62.7, 2, 1], [6, 0, 26, 2, 0],
  [0, 7, 18.5, 1, 0], [1, 6, 14.3, 1, 0], [2, 5, 26.8, 2, 0], [3, 4, 20.1, 3, 0],
  [4, 3, 14.3, 3, 0], [5, 2, 15, 0, 0], [6, 1, 12.7, 3, 0], [7, 0, 17.3, 0, 0],
  [1, 7, 28.9, 0, 0], [2, 6, 30, 2, 0], [3, 5, 75.4, 3, 1], [4, 4, 27.3, 2, 0],
  [5, 3, 18.8, 3, 0], [6, 2, 10.3, 0, 0], [7, 1, 24.3, 1, 0], [2, 7, 19.2, 1, 0],
  [3, 6, 20.4, 2, 0], [4, 5, 13.1, 1, 0], [5, 4, 65.6, 2, 1], [6, 3, 12.3, 0, 0],
  [7, 2, 25.2, 1, 0], [3, 7, 22.2, 0, 0], [4, 6, 27.2, 2, 0], [5, 5, 13.8, 2, 0],
  [6, 4, 16.7, 1, 0], [7, 3, 18.7, 0, 0], [4, 7, 15.5, 3, 0], [5, 6, 18.1, 2, 0],
  [6, 5, 26.3, 0, 0], [7, 4, 71.3, 3, 1], [5, 7, 19.2, 1, 0], [6, 6, 14.2, 1, 0],
  [7, 5, 13.5, 1, 0], [6, 7, 28.2, 0, 0], [7, 6, 18.5, 3, 0], [7, 7, 18.3, 1, 0],
];

/** A ground point (a, b in grid steps, halves on the roads) on screen. */
function ground(a: number, b: number) {
  return {
    x: ORIGIN.x + STEP_X * (a - b),
    y: ORIGIN.y - HALF_DEPTH + STEP_Y * (a + b),
  };
}

function building(a: number, b: number, height: number) {
  const cx = ORIGIN.x + STEP_X * (a - b);
  const base = ORIGIN.y + STEP_Y * (a + b);
  const side = base - HALF_DEPTH;
  const left = cx - HALF_WIDTH;
  const right = cx + HALF_WIDTH;
  return {
    left: `M${left} ${side}L${cx} ${base}V${base - height}L${left} ${side - height}Z`,
    right: `M${right} ${side}L${cx} ${base}V${base - height}L${right} ${side - height}Z`,
    roof: `M${cx} ${side - HALF_DEPTH - height}L${right} ${side - height}L${cx} ${base - height}L${left} ${side - height}Z`,
    /** The left wall's top corner, where its windows start. */
    wall: { x: left, y: side - height },
  };
}

/** The three windows high on a left wall, as Figma places them. */
const WINDOW_OFFSETS = [
  [4.4, 11.2],
  [11.8, 14.9],
  [19.3, 18.7],
] as const;

function windowPath(wall: { x: number; y: number }, index: number) {
  const [dx, dy] = WINDOW_OFFSETS[index] ?? WINDOW_OFFSETS[0];
  const x = wall.x + dx;
  const y = wall.y + dy;
  return `M${x} ${y}L${x + 3.9} ${y + 2}V${y + 8}L${x} ${y + 6}Z`;
}

/** Roads run between the blocks, on the half steps; the ground reaches
 *  0.4 of a step past the outer blocks. */
const ROAD_LINES = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5, 6.5];
const GROUND_FROM = -0.4;
const GROUND_TO = 7.4;

/** Windows that light up and go out, each on its own beat (none of them
 *  hidden behind a building in front). */
const LIT_WINDOWS: ReadonlyArray<{ a: number; b: number; index: number; seconds: number; delay: number }> = [
  { a: 1, b: 5, index: 2, seconds: 7, delay: 1.8 },
  { a: 4, b: 2, index: 0, seconds: 9, delay: 2.6 },
  { a: 5, b: 1, index: 1, seconds: 6, delay: 3.3 },
  { a: 3, b: 5, index: 2, seconds: 8, delay: 2.2 },
  { a: 0, b: 1, index: 0, seconds: 10, delay: 4.1 },
  { a: 7, b: 4, index: 1, seconds: 7.5, delay: 3.7 },
];

const HEIGHTS = new Map(BUILDINGS.map(([a, b, height]) => [`${String(a)}-${String(b)}`, height]));

function heightOf(a: number, b: number): number {
  return HEIGHTS.get(`${String(a)}-${String(b)}`) ?? 0;
}

function percent(value: number, from: number, size: number): string {
  return `${(((value - from) / size) * 100).toFixed(3)}%`;
}

/** A box centred on (x, y), in percentages of the city, so it scales with
 *  it. */
function box(x: number, y: number, width: number, height: number): CSSProperties {
  return {
    left: percent(x - width / 2, VIEW.x, VIEW.width),
    top: percent(y - height / 2, VIEW.y, VIEW.height),
    width: `${((width / VIEW.width) * 100).toFixed(3)}%`,
    height: `${((height / VIEW.height) * 100).toFixed(3)}%`,
  };
}

/** The city's drawn layers share one box and one coordinate space. */
function Layer({ children }: { readonly children: ReactNode }) {
  return (
    <svg
      viewBox={`${String(VIEW.x)} ${String(VIEW.y)} ${String(VIEW.width)} ${String(VIEW.height)}`}
      fill="none"
      className="login-city-layer"
    >
      {children}
    </svg>
  );
}

const GROUND_PLATE = (() => {
  const top = ground(GROUND_FROM, GROUND_FROM);
  const right = ground(GROUND_TO, GROUND_FROM);
  const bottom = ground(GROUND_TO, GROUND_TO);
  const left = ground(GROUND_FROM, GROUND_TO);
  return `M${String(top.x)} ${String(top.y)}L${String(right.x)} ${String(right.y)}L${String(bottom.x)} ${String(bottom.y)}L${String(left.x)} ${String(left.y)}Z`;
})();

export function LoginCityArtwork() {
  const depot = ground(DEPOT.a, DEPOT.b);

  return (
    <div className="login-city" data-node-id="89:1577">
      <Layer>
        <g className="login-city-ground">
          <path className="login-city-ground-plate" d={GROUND_PLATE} />
          {ROAD_LINES.flatMap((line) => {
            const alongA = [ground(GROUND_FROM, line), ground(GROUND_TO, line)] as const;
            const alongB = [ground(line, GROUND_FROM), ground(line, GROUND_TO)] as const;
            return [alongA, alongB].map(([from, to], index) => (
              <line
                key={`${String(line)}-${String(index)}`}
                className="login-city-road"
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
              />
            ));
          })}
        </g>
      </Layer>

      <div className="login-city-live">
        {/* The depot's rings, on the ground: buildings in front hide them.
            Drawn at the wide ring's size (Figma's second) and grown from
            the small one's. */}
        {[0, 1].map((ring) => (
          <span
            key={ring}
            className="login-city-ring"
            style={{ ...box(depot.x, depot.y, 366.08, 183.04), animationDelay: `${String(1.2 + ring * 1.6)}s` }}
          />
        ))}
      </div>

      <Layer>
        {BUILDINGS.map(([a, b, height, roof, windows]) => {
          const shape = building(a, b, height);
          const depotBuilding = roof === "depot";
          const steps = Math.abs(a - DEPOT.a) + Math.abs(b - DEPOT.b);
          return (
            <g
              key={`${String(a)}-${String(b)}`}
              className={depotBuilding ? "login-city-building login-city-depot" : "login-city-building"}
              style={{ animationDelay: `${String(120 + steps * 70)}ms` }}
            >
              <path className="login-city-wall-left" d={shape.left} />
              <path className="login-city-wall-right" d={shape.right} />
              <path className="login-city-roof" data-tone={roof} d={shape.roof} />
              {windows === 1
                ? WINDOW_OFFSETS.map((_, index) => (
                    <path key={index} className="login-city-window" d={windowPath(shape.wall, index)} />
                  ))
                : null}
            </g>
          );
        })}
      </Layer>

      <div className="login-city-live">
        {LIT_WINDOWS.map(({ a, b, index, seconds, delay }) => {
          const { wall } = building(a, b, heightOf(a, b));
          const [dx, dy] = WINDOW_OFFSETS[index] ?? WINDOW_OFFSETS[0];
          return (
            <span
              key={`${String(a)}-${String(b)}`}
              className="login-city-window-lit"
              style={{
                ...box(wall.x + dx + 1.95, wall.y + dy + 4, 3.9, 8),
                animationDuration: `${String(seconds)}s`,
                animationDelay: `${String(delay)}s`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
